/**
 * @fileoverview useCategories - Category management hook.
 *
 * Manages category data fetching and CRUD operations.
 * Automatically refreshes when budget context changes.
 *
 * @description Provides:
 * - Category list loading and caching
 * - Add new categories
 * - Cease/reactivate categories
 * - Delete future-dated categories
 * - Automatic budget context subscription
 *
 * Category lifecycle:
 * - Active: Can be used in transactions
 * - Ceased: Has end date, cannot be used in new transactions
 * - Future-dated: Can be deleted if never used
 *
 * @module hooks/useCategories
 */

import { useState, useEffect, useCallback } from 'react';
import { categoryAPI, onBudgetContextChange, transactionAPI } from '../services/api';
import { useRealtime } from '../contexts/RealtimeContext';

/**
 * Hook for managing budget categories.
 *
 * @param {number} currentUserId - Current user's ID for filtering own actions in realtime updates
 * @returns {Object} Category state and operations
 * @returns {Array} return.categories - All categories for current budget
 * @returns {boolean} return.isLoading - Whether categories are loading
 * @returns {Function} return.addCategory - Add a new category
 * @returns {Function} return.ceaseCategory - Cease a category
 * @returns {Function} return.reactivateCategory - Reactivate ceased category
 * @returns {Function} return.deleteCategory - Delete future-dated category
 * @returns {Function} return.loadCategories - Manually refresh categories
 */
export const useCategories = (currentUserId = null) => {
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

    // Realtime context for live updates
    // getSocketId used to filter own actions by socket ID (enables same-user multi-device sync)
    const { subscribeToEvent, isConnected, getSocketId } = useRealtime();

    // Load categories from API
    const loadCategories = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await categoryAPI.getAll();
            setCategories(data);
            setHasLoadedOnce(true);
        } catch (error) {
            // Error handling
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Add new category
    const addCategory = async (categoryName, startDate, startingBalance = null, allowExpense = false, allowTransfer = false) => {
        try {
            const newCategory = await categoryAPI.create(categoryName, startDate, startingBalance, allowExpense, allowTransfer);
            // Sort by sortOrder to maintain user-defined order, then by name as tiebreaker
            setCategories(prev => [...prev, newCategory].sort((a, b) =>
                (a.sortOrder || 0) - (b.sortOrder || 0) || a.categoryName.localeCompare(b.categoryName)
            ));
            return newCategory;
        } catch (error) {
            // Error handling
            throw error;
        }
    };

    // Cease a category
    const ceaseCategory = async (categoryKey) => {
        try {
            const updated = await categoryAPI.cease(categoryKey);
            setCategories(prev =>
                prev.map(cat => cat.categoryKey === categoryKey ? updated : cat)
            );
            return updated;
        } catch (error) {
            // Error handling
            throw error;
        }
    };

    // Reactivate a category
    const reactivateCategory = async (categoryKey) => {
        try {
            const updated = await categoryAPI.reactivate(categoryKey);
            setCategories(prev =>
                prev.map(cat => cat.categoryKey === categoryKey ? updated : cat)
            );
            return updated;
        } catch (error) {
            // Error handling
            throw error;
        }
    };

    // Delete a future-dated category
    const deleteCategory = async (categoryKey) => {
        try {
            await categoryAPI.delete(categoryKey);
            setCategories(prev =>
                prev.filter(cat => cat.categoryKey !== categoryKey)
            );
        } catch (error) {
            // Error handling
            throw error;
        }
    };

    // Update a category's name and/or starting balance and/or transaction type flags
    const updateCategory = async (categoryKey, categoryName, startingBalance, allowExpense, allowTransfer) => {
        try {
            await categoryAPI.update(categoryKey, categoryName, startingBalance, allowExpense, allowTransfer);
            // Reload categories to get updated data
            await loadCategories();
        } catch (error) {
            // Error handling
            throw error;
        }
    };

    // Load on mount - but only if we have a budget context
    useEffect(() => {
        // Wait for budget context to be set before loading
        if (transactionAPI.getCurrentBudgetId()) {
            loadCategories();
        }

        // Subscribe to budget context changes and reload when it's set
        const unsubscribe = onBudgetContextChange((budgetId) => {
            if (budgetId) {
                // Clear stale categories immediately to prevent flash of old data
                setCategories([]);
                setHasLoadedOnce(false);
                loadCategories();
            }
        });

        return unsubscribe;
    }, [loadCategories]);

    // Subscribe to realtime category events
    useEffect(() => {
        if (!subscribeToEvent || !isConnected) return;

        // Handle category created by another user/device
        const unsubscribeCreated = subscribeToEvent('category:created', (data) => {
            const { category, sourceSocketId } = data;

            // Skip if this event came from our own socket (we already added it optimistically)
            // Using socket ID instead of userId allows same-user multi-device sync
            const mySocketId = getSocketId();
            if (sourceSocketId && mySocketId && sourceSocketId === mySocketId) {
                return;
            }

            setCategories(prev => {
                // Avoid duplicates - use String() for type-safe comparison
                if (prev.some(c => String(c.categoryKey) === String(category.categoryKey))) return prev;
                // Sort by sortOrder to maintain user-defined order, then by name as tiebreaker
                return [...prev, category].sort((a, b) =>
                    (a.sortOrder || 0) - (b.sortOrder || 0) || a.categoryName.localeCompare(b.categoryName)
                );
            });
        });

        // Handle category updated by another user/device
        const unsubscribeUpdated = subscribeToEvent('category:updated', (data) => {
            const { category, sourceSocketId } = data;

            // Skip if this event came from our own socket
            const mySocketId = getSocketId();
            if (sourceSocketId && mySocketId && sourceSocketId === mySocketId) {
                return;
            }

            // If sort order changed, reload all categories
            if (category.sortOrderChanged) {
                loadCategories();
                return;
            }

            // Update the specific category
            setCategories(prev => {
                const existingIndex = prev.findIndex(c => String(c.categoryKey) === String(category.categoryKey));
                if (existingIndex >= 0) {
                    const updated = [...prev];
                    // Merge the update with existing data (update may be partial)
                    updated[existingIndex] = { ...updated[existingIndex], ...category };
                    return updated;
                }
                // Category not found in list - might have been added while we were away
                return prev;
            });
        });

        // Handle category deleted by another user/device
        const unsubscribeDeleted = subscribeToEvent('category:deleted', (data) => {
            const { categoryKey, sourceSocketId } = data;

            // Skip if this event came from our own socket
            const mySocketId = getSocketId();
            if (sourceSocketId && mySocketId && sourceSocketId === mySocketId) {
                return;
            }

            setCategories(prev =>
                prev.filter(c => String(c.categoryKey) !== String(categoryKey))
            );
        });

        return () => {
            unsubscribeCreated();
            unsubscribeUpdated();
            unsubscribeDeleted();
        };
    // isConnected in deps ensures we re-subscribe when socket connects
    // getSocketId used for filtering own actions (same-user multi-device sync)
    }, [subscribeToEvent, getSocketId, loadCategories, isConnected]);

    return {
        categories,
        isLoading,
        hasLoadedOnce,
        addCategory,
        ceaseCategory,
        reactivateCategory,
        deleteCategory,
        updateCategory,
        reloadCategories: loadCategories
    };
};